import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useEditMode } from '@/contexts/EditModeContext';
import { DCA_LINKS } from '@/lib/dcaConfig';

const PricingCard = ({ plan }) => {
  const { isEditMode, sendEditMessage } = useEditMode();
  const isInternalLink = plan.button_link?.startsWith('/');
  const isFreeConsultation = plan.title?.toLowerCase().includes('consultation') || plan.title?.toLowerCase().includes('strategy session') && plan.price === 'FREE';

  const handleEditClick = (e) => {
    if (isEditMode) {
      e.preventDefault();
      e.stopPropagation();
      sendEditMessage('pricing', plan.id, plan);
    }
  };

  const handleButtonClick = (e) => {
    if (isFreeConsultation && !isEditMode) {
      e.preventDefault();
      window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Card 
      className={`relative flex flex-col hover:shadow-xl transition-all duration-300 group ${
        plan.is_featured ? 'border-primary border-2 scale-105 shadow-lg' : ''
      } ${isEditMode ? 'cursor-pointer' : ''}`}
      onClick={handleEditClick}
      data-testid={`pricing-card-${plan.title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {isEditMode && (
        <div className="absolute inset-0 border-2 border-dashed border-transparent group-hover:border-blue-500 rounded-lg transition-all duration-200 pointer-events-none z-10">
          <div className="absolute -top-8 left-0 bg-blue-500 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Edit: {plan.title}
          </div>
        </div>
      )}
      
      {plan.is_featured && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground">
          Most Popular
        </Badge>
      )}
      
      <CardHeader className="text-center pb-2 pt-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <h3 className="text-lg font-heading font-bold text-muted-foreground uppercase">
          {plan.title}
        </h3>
        <div className="mt-2">
          <span className="text-4xl font-heading font-bold text-foreground">
            {plan.price}
          </span>
          <span className="text-muted-foreground ml-1">/{plan.duration}</span>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 pt-4">
        <ul className="space-y-3">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2">
              <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-sm text-muted-foreground">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      
      <CardFooter className="pt-4">
        {isFreeConsultation ? (
          <Button 
            className={`w-full ${plan.is_featured ? '' : 'bg-secondary hover:bg-secondary/90'}`}
            data-testid={`pricing-cta-${plan.title.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={handleButtonClick}
          >
            {plan.button_text}
          </Button>
        ) : isInternalLink ? (
          <Button 
            asChild 
            className={`w-full ${plan.is_featured ? '' : 'bg-secondary hover:bg-secondary/90'}`}
            data-testid={`pricing-cta-${plan.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Link to={isEditMode ? '#' : plan.button_link} onClick={isEditMode ? handleEditClick : undefined}>
              {plan.button_text}
            </Link>
          </Button>
        ) : (
          <Button 
            asChild 
            className={`w-full ${plan.is_featured ? '' : 'bg-secondary hover:bg-secondary/90'}`}
            data-testid={`pricing-cta-${plan.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <a href={isEditMode ? '#' : plan.button_link} target={isEditMode ? undefined : "_blank"} rel="noopener noreferrer" onClick={isEditMode ? handleEditClick : undefined}>
              {plan.button_text}
            </a>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default PricingCard;
